export type AppRole = "admin" | "manager" | "teacher" | "student" | "parent";

export type AppUser = {
  id: string;
  email: string;
  name: string;
  role: AppRole;
};

/** Admin and manager are equivalent for every operational task, as in the LMS. */
export function isManagement(role: AppRole) {
  return role === "admin" || role === "manager";
}

export type MasteryLevel = "emerging" | "developing" | "secure" | "transfer";
export type EvidenceKind = "photo" | "voice" | "writing" | "observation" | "coach_note";
export type PlanStatus = "pending" | "done" | "skipped";

export const masteryLevels: MasteryLevel[] = ["emerging", "developing", "secure", "transfer"];

export const masteryLabels: Record<MasteryLevel, string> = {
  emerging: "Emerging",
  developing: "Developing",
  secure: "Secure",
  transfer: "Transfer",
};

export const evidenceKindLabels: Record<EvidenceKind, string> = {
  photo: "Photo",
  voice: "Voice",
  writing: "Writing",
  observation: "Observation",
  coach_note: "Coach note",
};

export type EnrolmentStatus = "active" | "paused" | "completed";
/**
 * The two ways a family can do this.
 *
 * There used to be a third, `subject_support`, and it asked the wrong question
 * at the wrong time: a family had to decide at enrolment whether they would
 * need a tutor, before teaching a single lesson and therefore before they could
 * possibly know. Asking for a tutor is now an action inside the parent-led
 * route, taken per subject when the need is actually felt.
 */
export type LearningRoute = "parent_led" | "teacher_led";

/** One line of a weekly allocation: a subject and how many blocks it gets. */
export type SubjectBlocks = { subject: string; blocks: number };
export type CurriculumPathway = "balanced" | "pakistan" | "cambridge" | "faith" | "future_skills";
export type DeliveryMode = "online" | "home" | "hybrid";

export type Learner = {
  id: string;
  guardian_id: string;
  profile_id: string | null;
  name: string;
  initials: string | null;
  date_of_birth: string | null;
  stage: string;
  /**
   * The level the child is working through — 'playgroup' … 'class-5'.
   *
   * Null on a learner created before levels existed; the app falls back to the
   * middle level of their old stage rather than showing them nothing.
   */
  level_slug: string | null;
  /** When they began this level. Progress counts days WORKED, not days elapsed. */
  level_started_on: string | null;
  avatar_color: string;
  is_active: boolean;
  /**
   * The rest of the child.
   *
   * All optional, and that is the design: a parent who wants to start today
   * types a name and starts. The profile fills in as the relationship does, and
   * every one of these fields is something a coach would otherwise have to ask
   * for twice.
   */
  gender: string | null;
  home_language: string | null;
  city: string | null;
  country: string | null;
  about: string | null;
  support_needs: string | null;
  previous_schooling: string | null;
  goals: string | null;
  started_on: string | null;
  weekly_target_minutes: number;
  enrolment_status: EnrolmentStatus;
  /** The service this child uses. Kept per learner because siblings may use different routes. */
  learning_route: LearningRoute | null;
  /** Every child now follows a pathway; balanced is the default for a parent who has no preference. */
  curriculum_pathway: CurriculumPathway | null;
  preferred_delivery: DeliveryMode | null;
  /** Subjects this family has asked for tutoring help with. Applies to both routes. */
  supported_subjects: string[];
  /**
   * The family's own block allocation, when they have adjusted the pathway.
   *
   * NULL means the chosen pathway's published default is in use, which is a
   * different fact from an empty array — an empty array would mean a family
   * deliberately allocated nothing.
   */
  pathway_blocks: SubjectBlocks[] | null;
};

/** Everything a parent can set about their child. The id and guardian are not theirs to change. */
export type LearnerProfileInput = Omit<
  Learner,
  "id" | "guardian_id" | "profile_id" | "initials" | "is_active"
>;

/** The parent's own details, once per parent rather than once per child. */
export type GuardianDetails = {
  profile_id: string;
  phone: string | null;
  city: string | null;
  country: string | null;
  approach: string | null;
  looking_for: string | null;
  how_heard: string | null;
  coach_sharing_default: boolean;
};

/** A programme-private note about a learner. Never shown to the family. */
export type LearnerNote = {
  id: string;
  learner_id: string;
  author_id: string;
  body: string;
  created_at: string;
};

/**
 * One piece of learning that took time.
 *
 * Separate from a completed plan item because they answer different questions:
 * the plan item says the activity was done, the session says when and for how
 * long — which is what a family is asked for when someone official wants to
 * know what their child did this term.
 */
export type Session = {
  id: string;
  learner_id: string;
  plan_day_id: string | null;
  plan_item_id: string | null;
  activity_id: string | null;
  source: "activity" | "manual";
  title: string | null;
  minutes: number;
  note: string | null;
  occurred_at: string;
  recorded_by: string;
};

export type SuggestionKind = "activity" | "resource" | "improvement" | "question" | "idea";
export type SuggestionStatus = "pending" | "accepted" | "declined";

export const suggestionKinds: SuggestionKind[] = [
  "activity",
  "resource",
  "improvement",
  "question",
  "idea",
];

/** Something a family sent back to the programme, and the programme's answer. */
export type Suggestion = {
  id: string;
  learner_id: string | null;
  activity_id: string | null;
  kind: SuggestionKind;
  title: string;
  body: string;
  url: string | null;
  storage_path: string | null;
  file_name: string | null;
  mime_type: string | null;
  status: SuggestionStatus;
  admin_response: string | null;
  reviewed_at: string | null;
  created_by: string;
  created_at: string;
};

/** One row of `homeschool_learner_overview`: progress, hours and backlog per child. */
export type LearnerOverview = {
  learner_id: string;
  name: string;
  stage: string;
  avatar_color: string;
  initials: string | null;
  date_of_birth: string | null;
  started_on: string | null;
  enrolment_status: EnrolmentStatus;
  weekly_target_minutes: number;
  guardian_id: string;
  guardian_name: string | null;
  guardian_email: string | null;
  evidence_count: number;
  pending_reviews: number;
  last_evidence_at: string | null;
  minutes_30d: number;
  active_days_30d: number;
  last_session_at: string | null;
  mastery_percent: number | null;
  secure_competencies: number;
};

export type Competency = {
  id: string;
  code: string;
  title: string;
  domain: string;
  stage: string;
  description: string | null;
  next_step: string | null;
  sort_order: number;
  is_active: boolean;
  /** Framework strands this competency answers to, e.g. "EYFS · Mathematics". */
  standard_refs: string[];
};

export type Activity = {
  id: string;
  slug: string;
  title: string;
  area: string;
  stage: string;
  /**
   * Where this activity sits in the curriculum: which level, which term of it,
   * and where in that term's running order.
   *
   * All three are null until an author places the activity. An unplaced one is
   * still live and still reachable through the old stage pool, which is what
   * lets the library keep working while levels are filled in.
   */
  level_slug: string | null;
  term_index: number | null;
  sequence_order: number | null;
  icon: string;
  color: string;
  duration_minutes: number;
  prompt: string;
  say_this: string | null;
  observe_for: string | null;
  evidence_hint: string | null;
  parent_move: string | null;
  child_move: string | null;
  /**
   * The physical steps for THIS activity.
   *
   * Step 1 of the guide used to be one sentence from the UI dictionary, shown
   * identically on all 57 activities — the one step that should say what to do
   * said nothing specific. Null still falls back to that sentence, so an
   * un-authored activity degrades to the old behaviour rather than a blank.
   */
  what_to_do: string | null;
  /** A short worked scene: the activity as it really goes, wobble included. */
  story: string | null;
  default_evidence_kind: EvidenceKind;
  is_active: boolean;
  sort_order: number;
  /** The language this activity teaches in — not the language it is read in. */
  teaching_language: TeachingLanguage;
  /**
   * Which of the day this activity belongs to.
   *
   * 'core' is one of the six, sequenced through the term. 'enrichment' is an
   * optional extra offered beside the day — music, money, movement, machines —
   * which is never required and never counted towards the day being done.
   */
  track: ActivityTrack;
  /**
   * The age band an enrichment activity was written for. Null on core
   * activities, which are placed by level and term instead.
   */
  band: EnrichmentBand | null;
  /** Competency ids this activity can evidence, from the join table. */
  competency_ids: string[];
  resources: ActivityResource[];
  /**
   * The authored English, kept when a translation has been overlaid.
   *
   * An activity that teaches English words has to show those words in English
   * even to an Urdu reader, so the guide needs both texts at once — the
   * translation to understand, the original to say.
   */
  original?: { title: string; prompt: string; say_this: string | null };
  /** Display label only; area stays canonical for pathway and subject matching. */
  area_label?: string;
};

export type ActivityTrack = "core" | "enrichment";
export type EnrichmentBand = "early" | "middle" | "upper";

export type TeachingLanguage = "any" | "en" | "ur" | "ar";

export const teachingLanguageNames: Record<TeachingLanguage, string> = {
  any: "",
  en: "English",
  ur: "اردو",
  ar: "العربية",
};

/**
 * The medium of a resource, which is what decides how the app renders it.
 *
 * Whether the resource is an *example* — a photograph of the finished tally
 * chart, a recording of the rhyme said well — is a separate axis, because an
 * example can be any of these. It lives in `is_example`.
 */
export type ResourceKind =
  | "worksheet"
  | "pdf"
  | "image"
  | "video"
  | "audio"
  | "text"
  | "link"
  | "reading"
  | "document";

export const resourceKinds: ResourceKind[] = [
  "worksheet",
  "pdf",
  "image",
  "video",
  "audio",
  "text",
  "link",
  "reading",
  "document",
];

/** Where `storage_path` points: R2 behind the Worker, or the original Supabase bucket. */
export type StorageProvider = "supabase" | "r2";

export type ActivityResource = {
  id: string;
  activity_id: string;
  kind: ResourceKind;
  title: string;
  /** Exactly one of `url`, `storage_path` and `body` is set — the table enforces it. */
  url: string | null;
  storage_path: string | null;
  body: string | null;
  storage_provider: StorageProvider;
  mime_type: string | null;
  size_bytes: number | null;
  file_name: string | null;
  is_example: boolean;
  note: string | null;
  sort_order: number;
};

/** A resource that renders in the page rather than opening somewhere else. */
export function isInlineResource(resource: ActivityResource) {
  if (resource.kind === "text") return true;
  if (!resource.storage_path) return false;
  return resource.kind === "image" || resource.kind === "video" || resource.kind === "audio";
}

export type PlanItem = {
  id: string;
  plan_day_id: string;
  activity_id: string;
  sort_order: number;
  status: PlanStatus;
  completed_at: string | null;
};

export type PlanDay = {
  id: string;
  learner_id: string;
  plan_date: string;
  note: string | null;
  items: PlanItem[];
};

export type Evidence = {
  id: string;
  learner_id: string;
  activity_id: string | null;
  plan_item_id: string | null;
  kind: EvidenceKind;
  title: string;
  note: string | null;
  storage_path: string | null;
  domain: string | null;
  shared_with_coach: boolean;
  review_status: "pending" | "approved" | "changes_requested";
  coach_feedback: string | null;
  /** When Al Noor reviewed it — what "new feedback" is measured against. */
  reviewed_at?: string | null;
  occurred_at: string;
  created_at: string;
  /** Competency ratings attached to this evidence. */
  ratings: { competency_id: string; rating: MasteryLevel }[];
};

export type MasteryRow = {
  learner_id: string;
  competency_id: string;
  evidence_count: number;
  percent: number;
  level: MasteryLevel;
  last_evidence_at: string | null;
};
